/**
 * SuperAdminGuard — Route guard for super-admin pages.
 *
 * D1: Tenant gate (server-side enforced, frontend guard is defense-in-depth).
 * - Super-admin tokens have tenant_id of "IDI"
 * - Dealer customer/profile/terms entities NOT resolvable by API for super-admin users
 * - Privacy banner + locked tiles in UI
 * - NO impersonation of any kind
 *
 * Server-side enforcement:
 * - Backend middleware checks tenant_id from JWT
 * - Super-admin queries against dealer tables return 403
 * - This frontend guard is a UX convenience, NOT the security boundary
 */

import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const SUPER_ADMIN_TENANT = 'IDI';
const SUPER_ADMIN_ROLES = ['super_admin', 'idi_admin'];

/**
 * Check if the current user is a super-admin.
 * @param {object} user - User from AuthContext
 * @returns {boolean}
 */
export function isSuperAdmin(user) {
  if (!user) return false;
  // Check tenant
  if (user.tenantId === SUPER_ADMIN_TENANT || user.tenant_id === SUPER_ADMIN_TENANT) return true;
  // Check role
  if (SUPER_ADMIN_ROLES.includes(user.role)) return true;
  // Check Cognito groups (primary role mechanism; backend reads cognito:groups)
  if (Array.isArray(user.groups) && user.groups.some((g) => SUPER_ADMIN_ROLES.includes(g))) return true;
  return false;
}

/**
 * Route guard component — wraps super-admin routes.
 * Redirects non-super-admin users to dashboard.
 */
export default function SuperAdminGuard() {
  const { user } = useAuth();

  if (!isSuperAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
